import type { InputHTMLAttributes } from 'react'
import { Text } from './typography'

interface RadioInputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
}

const CIRCLE_BASE = [
  'relative flex shrink-0 mr-3 items-center justify-center size-6 rounded-full border transition',
  'peer-checked:border-2',
  'after:absolute after:hidden after:size-2.25 after:rounded-full',
  'peer-checked:after:block',
].join(' ')

const CIRCLE_ACTIVE = [
  'bg-white border-neutral-100',
  'peer-checked:border-indigo-base',
  'after:bg-indigo-base',
  'group-hover:border-indigo-100 group-hover:shadow-radio-hover',
  'group-has-focus:border-0 group-has-focus:ring-2 group-has-focus:ring-indigo-base',
].join(' ')

const CIRCLE_DISABLED = [
  'bg-neutral-50 border-neutral-300',
  'peer-checked:border-neutral-300 peer-checked:bg-white',
  'after:bg-neutral-300',
].join(' ')

export function RadioInput({
  label,
  disabled,
  checked,
  className = '',
  ...props
}: RadioInputProps) {
  if (!label) {
    if (!checked && !disabled) {
      return (
        <span
          className={[
            'flex size-6 shrink-0 items-center justify-center rounded-full border border-neutral-100 bg-white transition',
            className,
          ]
            .filter(Boolean)
            .join(' ')}
        />
      )
    }
    const ringColor = disabled ? 'border-indigo-100' : 'border-indigo-base'
    return (
      <span
        className={[
          `relative flex size-6 shrink-0 items-center justify-center rounded-full border-8 transition ${ringColor}`,
          className,
        ]
          .filter(Boolean)
          .join(' ')}
      ></span>
    )
  }

  return (
    <label
      className={`group flex items-center justify-start gap-0 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <input
        type="radio"
        disabled={disabled}
        checked={checked}
        className="peer size-0"
        {...props}
      />
      <span
        className={`${CIRCLE_BASE} ${disabled ? CIRCLE_DISABLED : CIRCLE_ACTIVE} ${className}`}
      />
      <Text className={`${disabled ? 'text-neutral-300' : 'text-neutral-900'}`}>
        {label}
      </Text>
    </label>
  )
}
